/**
 * The files uploaded on an enquiry's RFQ page — kept on the server, and listed back.
 *
 * "Upload list" reads a customer's list in the browser and adds its lines. Until 2026-11-03 the
 * file itself was then thrown away, so there was nothing to check the lines against later (Akhil:
 * "option to view manually uploaded RFQ files").
 *
 * ---- quoflow's own pair (2026-11-03) ----
 *
 * `enquiry.upload_file` (form: `enquiry`, `file`) and `enquiry.get_files`, asked of the Frappe
 * team the same day. The SERVER makes the file private and attaches it to the enquiry, and checks
 * the user may read that enquiry — the app sends nothing else, because nothing else should be up
 * to the caller. Probed on rfqdev: `get_files` answers the list below; `upload_file` refuses a
 * missing file, an `.exe` and an unknown enquiry (404).
 *
 * This replaced Frappe core `upload_file` with `doctype`/`docname`/`is_private` in the form,
 * which the Waves proxy had to allow whole and could not pin to an enquiry or to private.
 *
 * The file is stored as sent: same bytes, same name, same type, and read back only through
 * Frappe's permission-checked download (`fileHref`).
 */

import { useQuery } from '@tanstack/react-query'
import { callGet, callPostForm } from './bosun'
import type { WireList } from './wire'
import { fileHref } from './fileUrl'
import { toAttachmentKind } from './useMail'
import { humanSize } from '../mailUpload'
import type { MailAttachment } from '../types'

/** An uploaded file, in the shape the mail attachment cards and previews already take. */
export interface EnquiryFile extends MailAttachment {
  /** `YYYY-MM-DD HH:mm:ss`, site local time. */
  uploaded_at: string
}

export interface WireEnquiryFile {
  name?: unknown
  file_name?: unknown
  file_url?: unknown
  file_size?: unknown
  creation?: unknown
}

const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

export function enquiryUploadForm(enquiryId: string, file: File): FormData {
  const form = new FormData()
  form.append('enquiry', enquiryId)
  form.append('file', file, file.name)
  return form
}

export function toEnquiryFile(wire: WireEnquiryFile): EnquiryFile {
  const url = text(wire.file_url)
  const name = text(wire.file_name) || url.split('/').pop() || 'file'
  const bytes = typeof wire.file_size === 'number' ? wire.file_size : Number(wire.file_size)
  return {
    id: text(wire.name) || url,
    name,
    kind: toAttachmentKind(name, ''),
    size: Number.isFinite(bytes) ? humanSize(bytes) : '',
    preview_url: fileHref(url),
    // Microseconds dropped: nobody reads them, and the rest of the app shows seconds.
    uploaded_at: text(wire.creation).replace(/\.\d+$/, ''),
  }
}

export const enquiryFilesKey = (enquiryId: string) => ['enquiry-files', enquiryId] as const

export async function uploadEnquiryFile(enquiryId: string, file: File): Promise<EnquiryFile> {
  return toEnquiryFile(await callPostForm<WireEnquiryFile>('enquiry', 'upload_file', enquiryUploadForm(enquiryId, file)))
}

/** Every file uploaded on this enquiry, newest first. */
export function useEnquiryFiles(enquiryId: string | null | undefined) {
  return useQuery({
    queryKey: enquiryFilesKey(enquiryId ?? ''),
    queryFn: async () => {
      const page = await callGet<WireList<WireEnquiryFile>>('enquiry', 'get_files', { enquiry: enquiryId ?? '', limit: 50, offset: 0 })
      return page.data.map(toEnquiryFile)
    },
    enabled: !!enquiryId,
    staleTime: 60_000,
  })
}
